import React from "react";
import PropTypes from "prop-types";

import AuthenticationService from "service/auth/AuthenticationService";

import "./v2TopBar.scss";

/**
 * The revamped screens' top bar — Figma "Header" 4669:15571 and 4591:7, which
 * are the same 64px frame: a breadcrumb on the left and the signed-in user on
 * the right.
 *
 * WHY IT LIVES IN THE SHELL AND NOT IN EACH SCREEN. The board draws ONE bar.
 * Bridge drew two: its own toolbar (logo + a boxed name) and then, under it,
 * whatever breadcrumb row each v2 screen rendered for itself. Matching the
 * board therefore means putting the breadcrumb INTO the toolbar — and the
 * toolbar is `components/drawer/Drawer.js`, which wraps every screen in the
 * employee branch.
 *
 * So it is ROUTE-AWARE rather than restyled (Nittin's call): Drawer swaps in
 * these two pieces only on the v2 routes listed in `IS_V2`, and every legacy
 * screen keeps the toolbar it has today, unchanged. The left icon rail stays on
 * both.
 *
 * HOW THE CRUMBS GET HERE. They are per-screen data — a patient's name, which
 * section is open — and the shell cannot know them. Rather than a context that
 * every v2 screen would have to be wrapped in, a screen publishes its crumbs
 * with `setV2Crumbs(...)` and the bar subscribes. Module-scope, no provider, no
 * new dependency, and it survives the shell and the screen being in different
 * parts of the tree.
 */

/* ------------------------------------------------------------------ *
 * The crumb channel
 * ------------------------------------------------------------------ */

let current = null;
const listeners = new Set();

/**
 * Publish this screen's breadcrumb. Pass `null` on unmount — a stale crumb
 * naming the previous patient is worse than none, and this is the one piece of
 * chrome that outlives the screen that set it.
 *
 * `crumbs` is `[{ label, onClick? }]`; the last one renders as the current
 * page and is never a link.
 */
export const setV2Crumbs = (crumbs) => {
  current = crumbs && crumbs.length ? crumbs : null;
  listeners.forEach((fn) => fn(current));
};

const subscribe = (fn) => {
  listeners.add(fn);
  fn(current);
  return () => listeners.delete(fn);
};

/**
 * Which routes get the board's bar. Kept as one list so "is this a v2 screen"
 * is answered in a single place — the alternative is the same regex pasted into
 * the shell and into whatever comes next.
 */
const V2_ROUTES = [
  /^\/patients-v2/,
  /^\/profile-v2\//,
  /^\/appointments-v2\//,
  /^\/patient-record\//,
  /^\/plan-account\//,
];

export const isV2Route = (pathname) =>
  V2_ROUTES.some((re) => re.test(pathname || ""));

/* ------------------------------------------------------------------ *
 * The two pieces the shell renders
 * ------------------------------------------------------------------ */

const HomeIcon = () => (
  <svg className="v2tb-crumb-icon" width="18" height="18" viewBox="0 0 18 18"
       fill="none" aria-hidden="true">
    <path d="M11.25 13.5L6.75 9L11.25 4.5" stroke="currentColor"
          strokeWidth="1.3125" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** Left half of the bar (4669:15572) — an 18px icon, then the crumb trail. */
export class V2Crumbs extends React.Component {
  state = { crumbs: null };

  componentDidMount() {
    this._off = subscribe((crumbs) => this.setState({ crumbs }));
  }

  componentWillUnmount() {
    if (this._off) this._off();
  }

  render() {
    const { crumbs } = this.state;
    if (!crumbs) return null;
    const last = crumbs.length - 1;
    return (
      <nav className="v2tb-crumbs" aria-label="Breadcrumb">
        {crumbs.map((c, i) => [
          i ? (
            <span className="v2tb-sep" key={`s${i}`}>
              /
            </span>
          ) : null,
          i === last || !c.onClick ? (
            <b className="v2tb-now" key={c.label}>
              {c.label}
            </b>
          ) : (
            <button type="button" className="v2tb-link" key={c.label} onClick={c.onClick}>
              {i === 0 ? <HomeIcon /> : null}
              {c.label}
            </button>
          ),
        ])}
      </nav>
    );
  }
}

/** Right half (4669:15578) — a 32px avatar, name over role, and a chevron. */
/** Figma's icon/log-out, 17x15 (3015:8489). */
const LogOutIcon = () => (
  <svg
    className="v2tb-menu-icon"
    viewBox="0 0 17 15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9.5 1H3C1.9 1 1 1.9 1 3V12C1 13.1 1.9 14 3 14H9.5" />
    <path d="M7.5 7.5H15.5M12.7 11L15.5 7.5L12.7 4" />
  </svg>
);

/** Material "content_copy" / "check", drawn at the board's 14px. */
const CopyIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

/** "+91 98765 43210" — a ten-digit number grouped 5 + 5, as the board draws it. */
export const formatAccountPhone = (countryCode, phone) => {
  const digits = String(phone == null ? "" : phone).replace(/\D/g, "");
  if (!digits || /^0+$/.test(digits)) return "";
  const body = digits.length === 10 ? `${digits.slice(0, 5)} ${digits.slice(5)}` : digits;
  const cc = String(countryCode == null ? "" : countryCode).replace(/\D/g, "");
  return cc ? `+${cc} ${body}` : body;
};

/**
 * The two details the account menu shows, from the stored login profile — no
 * request. DOCTOR CODE is doctorProfile.code (api-user-service doc_profile), so
 * an employee login has none; the phone comes from the doctor profile, else the
 * employee profile, else the account.
 */
export const accountDetails = (profile) => {
  const p = profile || {};
  const doc = p.doctorProfile || {};
  const emp = p.employeeProfile || {};
  const code = String(doc.code || "").trim();
  const src = [doc, emp, p].find((x) => formatAccountPhone(null, x.phoneno)) || {};
  const phone = formatAccountPhone(src.countryCode || p.countryCode || 91, src.phoneno);
  return { code, phone };
};

/** Copies text, with the textarea fallback for a context without the async API. */
const copyText = (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) resolve();
    else reject(new Error("copy failed"));
  });
};

export class V2Account extends React.Component {
  static propTypes = { onClick: PropTypes.func };

  /* Clicking opens a menu. It used to do NOTHING — `<V2Account />` is rendered
     from the drawer with no onClick at all — so the chevron promised a dropdown
     that did not exist. Logging out is the one thing this chip is for, and it
     is otherwise only reachable from the Profile screen.

     The menu is Figma 3015:8489, "Menu · Account" (2026-09-17): the user's
     DOCTOR CODE (a chip with a copy button) and PHONE above a divider, then Log
     out — the same menu phr draws. A detail the profile lacks is not drawn;
     with neither, the menu is Log out alone. */
  state = { open: false, copied: false };

  componentDidMount() {
    document.addEventListener("mousedown", this._onDocDown);
    document.addEventListener("keydown", this._onKey);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this._onDocDown);
    document.removeEventListener("keydown", this._onKey);
    clearTimeout(this._copiedTimer);
    this._unmounted = true;
  }

  _onDocDown = (e) => {
    if (!this.state.open) return;
    const inTrigger = this._btn && this._btn.contains(e.target);
    const inMenu = this._menu && this._menu.contains(e.target);
    if (!inTrigger && !inMenu) this.setState({ open: false });
  };

  _onKey = (e) => {
    if (e.key === "Escape" && this.state.open) this.setState({ open: false });
  };

  _toggle = () => {
    this.setState((s) => ({ open: !s.open }));
    if (this.props.onClick) this.props.onClick();
  };

  /* "Copied" for a moment after the copy button is pressed — the glyph turns
     into a check and the button says so to assistive tech. */
  _copyCode = (code) => {
    copyText(code).then(
      () => {
        if (this._unmounted) return;
        this.setState({ copied: true });
        clearTimeout(this._copiedTimer);
        this._copiedTimer = setTimeout(() => this.setState({ copied: false }), 1500);
      },
      () => {}
    );
  };

  _logout = () => {
    /* AuthenticationService.logout() clears the session, the storage and every
       cookie, then sends the browser to "/" — so there is nothing to do after
       it and no state to tidy. */
    new AuthenticationService().logout();
  };

  render() {
    const auth = new AuthenticationService();
    const p = auth.getUserProfile() || {};
    const e = p.employeeProfile || p.doctorProfile || {};
    const name =
      [e.firstName, e.lastName].filter(Boolean).join(" ") || e.name || "";
    const initials =
      ((e.firstName || name || "?").charAt(0) + (e.lastName || "").charAt(0)).toUpperCase();
    const { code, phone } = accountDetails(p);
    const { open, copied } = this.state;

    return (
      <div className="v2tb-account-wrap">
        <button
          type="button"
          ref={(n) => { this._btn = n; }}
          className={`v2tb-account${open ? " is-open" : ""}`}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={this._toggle}
          aria-label={name ? `Account: ${name}` : "Account"}
        >
          {e.profileImage ? (
            <img className="v2tb-avatar" src={e.profileImage} alt="" />
          ) : (
            <span className="v2tb-avatar v2tb-avatar-initials">{initials}</span>
          )}
          {/* Name ALONE. The role line under it was dropped — it cost a
              /userroles request on every page to print a job title next to
              someone's own name, which they already know. */}
          <span className="v2tb-name">{name}</span>
          <svg className="v2tb-chev" width="9" height="5" viewBox="0 0 9 5" fill="none" aria-hidden="true">
            <path d="M1 1L4.5 4L8 1" stroke="currentColor" strokeWidth="1.2"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {open ? (
          <div
            className="v2tb-menu"
            role="menu"
            aria-label="Account"
            ref={(n) => { this._menu = n; }}
          >
            <div className="v2tb-menu-divider" aria-hidden="true" />
            {code || phone ? (
              <React.Fragment>
                <div className="v2tb-menu-details">
                  {code ? (
                    <div className="v2tb-menu-field">
                      <span className="v2tb-menu-label" id="v2tb-menu-code-label">Doctor code</span>
                      <span className="v2tb-menu-value-row">
                        <span className="v2tb-menu-chip" aria-labelledby="v2tb-menu-code-label">{code}</span>
                        <button
                          type="button"
                          className={`v2tb-menu-copy${copied ? " is-copied" : ""}`}
                          aria-label={copied ? "Doctor code copied" : "Copy doctor code"}
                          title={copied ? "Copied" : "Copy doctor code"}
                          onClick={() => this._copyCode(code)}
                        >
                          {copied ? <CheckIcon /> : <CopyIcon />}
                        </button>
                      </span>
                    </div>
                  ) : null}
                  {phone ? (
                    <div className="v2tb-menu-field is-tight">
                      <span className="v2tb-menu-label">Phone</span>
                      <span className="v2tb-menu-value">{phone}</span>
                    </div>
                  ) : null}
                </div>
                <div className="v2tb-menu-divider" aria-hidden="true" />
              </React.Fragment>
            ) : null}
            <div className="v2tb-menu-actions">
              <button type="button" role="menuitem" className="v2tb-menu-item" onClick={this._logout}>
                <LogOutIcon />
                Log out
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}
